import { ApiRequestOptions } from '@api/core/ApiRequestOptions';
import { getRequestOptions } from '@api/core/utils';

import type { ApplicationFormMailRequestViewModel } from '../models/ApplicationFormMailRequestViewModel';
import type { ComboViewModel } from '../models/ComboViewModel';
import type { CompliantFormMailRequestViewModel } from '../models/CompliantFormMailRequestViewModel';
import type { ContactFormMailRequestViewModel } from '../models/ContactFormMailRequestViewModel';
import type { SetairCompliantFormMailRequestViewModel } from '../models/SetairCompliantFormMailRequestViewModel';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class MailsService {
  /**
   * Send Mail (Contact Form)
   * Sends mail for contact form
   * @returns string Success
   * @throws ApiError
   */
  public static async sendMailContactForm(
    {
      body,
    }: {
      body?: ContactFormMailRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<string> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<string>({
      method: 'POST',
      url: `/mails/contact`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Send Mail (Application Form)
   * Sends mail for application form
   * @returns string Success
   * @throws ApiError
   */
  public static async sendMailApplicationForm(
    {
      body,
    }: {
      body?: ApplicationFormMailRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<string> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<string>({
      method: 'POST',
      url: `/mails/application`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Send Mail (Compliant Form)
   * Sends mail for compliant form
   * @returns string Success
   * @throws ApiError
   */
  public static async sendMailCompliantForm(
    {
      body,
    }: {
      body?: CompliantFormMailRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<string> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<string>({
      method: 'POST',
      url: `/mails/compliant`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Send Mail(Setair Compliant)
   * Sends mail for compliant form to setair
   * @returns string Success
   * @throws ApiError
   */
  public static async sendMailCompliantFormSetair(
    {
      body,
    }: {
      body?: SetairCompliantFormMailRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<string> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<string>({
      method: 'POST',
      url: `/mails/setair`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Get Subjects(Compliant Form)
   * Gets subjects for compliant form
   * @returns ComboViewModel Success
   * @throws ApiError
   */
  public static async getSubjects(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<ComboViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<Array<ComboViewModel>>({
      method: 'GET',
      url: `/mails/subjects`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Get Offices(Compliant Form)
   * Gets offices for compliant form
   * @returns ComboViewModel Success
   * @throws ApiError
   */
  public static async getOffices(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<ComboViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<Array<ComboViewModel>>({
      method: 'GET',
      url: `/mails/offices`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }
}
